import { isAxiosError } from 'axios';

import { api } from '@/lib/api';
import { getClientSessionOrThrow } from '@/lib/client-session-auth';
import type { UserData } from '@/store/profile.store';
import { EMAIL_VERIFICATION_COPY } from '../consts';
import type { EmailVerificationErrorKind } from '../types';
import { isForbiddenEmailDomainError, mapEmailCodeFailure, readApiErrorPayload } from '../utils';

export class EmailVerificationRequestError extends Error {
  readonly statusCode: number;
  readonly errorCode?: string;
  readonly errorKind: EmailVerificationErrorKind;

  constructor(
    message: string,
    statusCode: number,
    errorKind: EmailVerificationErrorKind,
    errorCode?: string,
  ) {
    super(message);
    this.name = 'EmailVerificationRequestError';
    this.statusCode = statusCode;
    this.errorKind = errorKind;
    this.errorCode = errorCode;
  }
}

export const failureFromPayload = (
  payload: unknown,
  intent: 'send' | 'change' | 'confirm',
  fallback: string,
) => {
  const parsed = readApiErrorPayload(payload);

  if (intent !== 'confirm' && isForbiddenEmailDomainError(parsed)) {
    return {
      errorKind: 'domain' as const,
      message:
        intent === 'change'
          ? EMAIL_VERIFICATION_COPY.domainForbiddenChange
          : EMAIL_VERIFICATION_COPY.domainForbidden,
      errorCode: parsed.code,
    };
  }

  if (intent === 'confirm') {
    const codeFailure = mapEmailCodeFailure(parsed);
    return { ...codeFailure, errorCode: parsed.code };
  }

  return {
    errorKind: 'unknown' as const,
    message: parsed.message || fallback,
    errorCode: parsed.code,
  };
};

export const extractUserFromPayload = (payload: unknown): Partial<UserData> | null => {
  const user = payload && typeof payload === 'object' && 'user' in payload ? payload.user : payload;
  if (!user || typeof user !== 'object') {
    return null;
  }

  return user as Partial<UserData>;
};

export const postCurrentUser = async (
  path: string,
  intent: 'send' | 'change' | 'confirm',
  fallback: string,
  body?: unknown,
): Promise<Partial<UserData>> => {
  const { accessToken } = await getClientSessionOrThrow();

  try {
    const response = await api.post(path, body, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    const user = extractUserFromPayload(response.data);
    if (!user) {
      throw new EmailVerificationRequestError(
        'Не удалось обновить почту',
        response.status,
        'unknown',
      );
    }

    return user;
  } catch (error) {
    if (error instanceof EmailVerificationRequestError) {
      throw error;
    }

    const statusCode = isAxiosError(error) ? (error.response?.status ?? 0) : 0;
    const payload = isAxiosError(error) ? error.response?.data : null;
    const failure = failureFromPayload(payload, intent, fallback);

    throw new EmailVerificationRequestError(
      failure.message,
      statusCode,
      failure.errorKind,
      failure.errorCode,
    );
  }
};
